/**
 * ReworkDialog — confirm + hint step for "Rework a section" (spec §5).
 * Native <dialog>, same pattern as the update-analysis dialog.
 */
import { useEffect, useRef, useState } from "react";
import { t, tVars } from "../../i18n";
import { formatWallClock } from "../../lib/timestamps";

// Frontend cost estimate scaled to window duration (spec §5: exactness not
// required). Whole-meeting analysis ≈ $0.25–0.50 for 60–90 min → ~$0.006/min,
// plus a small floor for the surrounding-context padding.
const REWORK_USD_PER_AUDIO_MIN = 0.006;

export function estimateReworkCost(startMs: number, endMs: number): string {
  const usd = Math.max(0.02, ((endMs - startMs) / 60000) * REWORK_USD_PER_AUDIO_MIN + 0.02);
  return usd.toFixed(2);
}

interface Props {
  open: boolean;
  startMs: number;
  endMs: number;
  paragraphCount: number;
  recordedAt: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (hint: string | null) => void;
}

export function ReworkDialog({ open, startMs, endMs, paragraphCount, recordedAt, busy, onCancel, onConfirm }: Props) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [hint, setHint] = useState("");
  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) { setHint(""); if (typeof dlg.showModal === "function") dlg.showModal(); }
    if (!open && dlg.open) { if (typeof dlg.close === "function") dlg.close(); }
  }, [open]);

  return (
    <dialog ref={ref} onClose={onCancel}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{ border: "1px solid var(--border, #374151)", borderRadius: 8,
        background: "var(--surface, #181b24)", color: "var(--text, #f9fafb)",
        padding: 20, minWidth: 420, maxWidth: "min(560px, 90vw)" }}>
      <form method="dialog" onSubmit={(e) => { e.preventDefault(); onConfirm(hint.trim() || null); }}>
        <h3 style={{ marginTop: 0 }}>{t("rework.title")}</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          {tVars("rework.readout", {
            from: formatWallClock(recordedAt, startMs),
            to: formatWallClock(recordedAt, endMs),
            n: paragraphCount,
            min: Math.max(1, Math.round((endMs - startMs) / 60000)),
          })}
        </p>
        <p style={{ fontSize: 13 }}>{t("rework.body")}</p>
        <label htmlFor="rework-hint" style={{ display: "block", marginTop: 12, marginBottom: 4, fontSize: 13 }}>
          {t("rework.hintLabel")}
        </label>
        <textarea id="rework-hint" dir="auto" rows={3} value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder={t("rework.hintPlaceholder")} disabled={busy}
          style={{ width: "100%", boxSizing: "border-box", padding: 8,
            background: "var(--surface-2, #21252f)", color: "var(--text, #f9fafb)",
            border: "1px solid var(--border, #374151)", borderRadius: 6,
            fontFamily: "inherit", fontSize: 14, resize: "vertical" }} />
        <p className="muted" style={{ fontSize: 12 }}>
          {tVars("rework.costEstimate", { cost: estimateReworkCost(startMs, endMs) })}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={busy}>
            {busy ? t("rework.sending") : t("rework.confirm")}
          </button>
        </div>
      </form>
    </dialog>
  );
}
